/* eslint-disable no-underscore-dangle */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
// adopt
import { adopt } from 'react-adopt';
// graphQL
import gql from 'graphql-tag';
// our components
import qs, { getQueryString } from 'helpers/queryStrings';
import RouterContext from 'helpers/routerContext';
import { Query } from 'components/common/Query';
import { PageInfo } from 'components/common/Paginator';
import { withApollo } from 'react-apollo';
import CreditBundlesList from './List';

/**
 * Query to fetch credit bundles from api
 */
export const CREDIT_BUNDLE_QUERY = gql`
  query CreditBundlesList(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $status: [FilterCreditBundleStatus]
    $createdOnGte: DateTime
    $createdOnLte: DateTime
    $paidAd: ID
    $agency: ID
    $userAgency: ID
  ) {
    creditBundles(
      first: $first
      after: $after
      last: $last
      before: $before
      status: $status
      createdOnGte: $createdOnGte
      createdOnLte: $createdOnLte
      paidAd: $paidAd
      agency: $agency
      userAgency: $userAgency
    ) {
      pageInfo {
        ...PaginatorPageInfo
      }
      edges {
        node {
          id
          status
          price
          currency
          description
          commission
          secondaryCurrency
          secondaryPrice
          rate
          user {
            id
            fakeId
            email
            fullname
          }
          paidads {
            edges {
              node {
                id
              }
            }
          }
        }
      }
    }
  }
  ${PageInfo}
`;

/**
 * Query to validate if ad can be added to a credit bundle
 */
export const CHECK_AD_WITH_BUNDLE_QUERY = gql`
  query($ad: ID!, $bundle: ID!) {
    checkAdWithBundle(ad: $ad, bundle: $bundle)
  }
`;

/**
 * Mutation to update a credit bundle
 */
export const UPDATE_USER_BUNDLE_MUTATION = gql`
  mutation($ad: ID!, $id: ID) {
    updateUserCreditBundle(input: { ad: $ad, id: $id }) {
      errors {
        field
        codes
        messages
      }
      creditBundle {
        id
      }
    }
  }
`;

/**
 * Mutation to cancel a credit bundle
 */
export const CANCEL_CREDIT_BUNDLE_MUTATION = gql`
  mutation($id: ID!) {
    cancelCreditBundle(input: { id: $id }) {
      creditBundle {
        status
        cancelledOn
        user {
          fullname
        }
      }
      errors {
        messages
      }
    }
  }
`;

/**
 * Default filters in PaidAdsList
 */
export const defaultFilters = {
  status: undefined,
  startDateGte: undefined,
  startDateLte: undefined,
  paidAd: undefined,
  agency: undefined,
  userAgency: undefined,
};

/**
 * @class
 * @extends Component
 */
class CreditBundlesListContainer extends Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    const { initialFilters } = props;

    const {
      numberOfRowsSelected,
      direction,
      cursor,
      ...initFilters
    } = initialFilters;
    this.state = {
      numberOfRowsSelected: parseInt(numberOfRowsSelected, 0) || 25,
      filters: {
        ...defaultFilters,
        ...initFilters,
      },
      pagination: { direction: direction || null, cursor: cursor || undefined },
    };
    this.Compose = adopt({
      query: this.listPackages,
    });
    this.fetchMore = () => {};
  }

  /**
   * @description
   * Query callback to compose with mutations.
   *
   * @param {*} render - Query's render props.
   * @returns {*} Query component.
   */
  listPackages = ({ render }) => {
    const { filters, numberOfRowsSelected, pagination } = this.state;
    const paginationVariables =
      !pagination.direction || pagination.direction === 'NEXT'
        ? {
            first: numberOfRowsSelected,
            after: pagination.cursor,
          }
        : {
            last: numberOfRowsSelected,
            before: pagination.cursor,
          };

    return (
      <Query
        query={CREDIT_BUNDLE_QUERY}
        variables={{
          ...filters,
          ...paginationVariables,
        }}
        notifyOnNetworkStatusChange
        fetchPolicy="cache-and-network"
      >
        {({ data, loading, fetchMore }) => {
          this.fetchMore = fetchMore;
          return render({ data, loading, fetchMore });
        }}
      </Query>
    );
  };

  /**
   * @description
   * Check if the ad can be added to a credit bundle.
   *
   * @param {Object} ad - Ad to check.
   * @param {Object} bundle - Bundle to check.
   * @param {Function} callback - Callback function to execute on error.
   * @returns {Function} - Function to execute the query.
   */
  checkAdWithBundle = async (ad, bundle, callback) => {
    const { client } = this.props;

    if (!ad || !bundle) return;

    const { errors } = await client.query({
      query: CHECK_AD_WITH_BUNDLE_QUERY,
      variables: { ad, bundle },
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    });

    const error = errors && errors.length > 0 ? errors[0] : null;
    callback(error);
  };

  /**
   * @description
   * Update a credit bundle.
   *
   * @param {Object} ad - New user.
   * @param {Object} bundle - Bundle to update.
   * @param {Function} callback - Callback function to execute.
   * @returns {Function} - Function to execute the query.
   */
  updateBundle = async (ad, bundle, callback) => {
    const { client } = this.props;

    if (!ad || !bundle) return;

    const { errors, creditBundle } = await client.mutate({
      mutation: UPDATE_USER_BUNDLE_MUTATION,
      variables: { ad, id: bundle },
    });
    callback(creditBundle, errors);
  };

  /**
   * @description
   * Cancel a credit bundle.
   *
   * @param {string} bundleId - Bundle ID to cancel.
   * @param {Function} callback - Callback function to execute.
   * @returns {Function} - Function to execute the mutation.
   */
  cancelCreditBundle = async (bundleId, callback) => {
    const { client } = this.props;

    if (!bundleId) return;

    try {
      const { data } = await client.mutate({
        mutation: CANCEL_CREDIT_BUNDLE_MUTATION,
        variables: { id: bundleId },
        refetchQueries: ['CreditBundlesList'],
      });

      const { creditBundle, errors } = data.cancelCreditBundle;
      callback(creditBundle, errors);
    } catch (error) {
      callback(null, [
        { messages: ['An error occurred while canceling the bundle'] },
      ]);
    }
  };

  /**
   * @description
   * Query helpful function to update query's result.
   *
   * @param {Object} previousResult - Latest Query result.
   * @param {Object} options - UpdateQuery options.
   * @returns {Object} New or Previous query's result.
   */
  updateQuery = (previousResult, options) => {
    const { pageInfo, edges: newEdges } = options.fetchMoreResult.creditBundles;
    if (newEdges.length !== 0) {
      return {
        creditBundles: {
          __typename: previousResult.creditBundles.__typename,
          edges: [...previousResult.creditBundles.edges, ...newEdges],
          pageInfo: {
            ...previousResult.creditBundles.pageInfo,
            hasNextPage: pageInfo.hasNextPage,
          },
        },
      };
    }
    return previousResult;
  };

  /**
   * @description
   * Handle change order.
   *
   * @param {Object} newOrder - Object that contain order mapping.
   */
  handleChangeOrder = newOrder => {
    const history = this.context;
    const {
      appliedOrder: { orderField, orderAsc },
    } = this.state;
    const appliedOrder = {
      orderField: newOrder.val,
      orderAsc: !(orderField === newOrder.val && orderAsc),
      orderColumn: newOrder.column,
    };
    this.setState({ appliedOrder });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({ ...parsed, appliedOrder });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Change number of rows selected.
   *
   * @param {number} selectedItem - New number of rows selected.
   */
  handleChangeNumberOfRows = selectedItem => {
    const history = this.context;
    this.setState({
      numberOfRowsSelected: selectedItem,
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({
      ...parsed,
      numberOfRowsSelected: selectedItem,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Fetch more rows when user change page.
   *
   * @param {Object} paginatorVariables - Pagination query's variables.
   */
  handleChangePage = ({ direction, cursor }) => {
    const history = this.context;
    this.setState({ pagination: { direction, cursor } });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({
      ...parsed,
      direction,
      cursor,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Filter rows according selected filters.
   *
   * @param {Object} filters - Filters to apply.
   */
  handleFilter = filters => {
    const history = this.context;
    this.setState({
      filters: { ...defaultFilters, ...filters },
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const { numberOfRowsSelected } = getQueryString(history);
    const stringified = qs.stringify({
      ...filters,
      numberOfRowsSelected,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Change state to restart filters.
   */
  handleRestartFilters = () => {
    const history = this.context;
    this.setState({
      filters: { ...defaultFilters },
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const { numberOfRowsSelected } = getQueryString(history);
    const stringified = qs.stringify({
      numberOfRowsSelected,
    });

    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Refresh the list.
   * @returns {Function} - Fetch More function.
   */
  handleRefresh = () => {
    this.fetchMore({
      updateQuery: (_, { fetchMoreResult }) => fetchMoreResult,
    });
  };

  /**
   * @description
   * Render AdsList component.
   */
  render() {
    const { me } = this.props;
    const { numberOfRowsSelected, filters, pagination } = this.state;
    return (
      <this.Compose>
        {({ query: { loading, data } }) => (
          <CreditBundlesList
            data={data}
            changeOrder={this.handleChangeOrder}
            numberOfRowsSelected={numberOfRowsSelected}
            paginationDirection={pagination.direction}
            changePage={paginationVariables => {
              this.handleChangePage(paginationVariables);
            }}
            refresh={this.handleRefresh}
            changeNumberOfRows={this.handleChangeNumberOfRows}
            loading={Object.keys(data).length === 0}
            fullLoading={loading}
            appliedFilter={filters}
            filter={this.handleFilter}
            restartFilters={this.handleRestartFilters}
            checkAdWithBundle={this.checkAdWithBundle}
            updateBundle={this.updateBundle}
            cancelCreditBundle={this.cancelCreditBundle}
            me={me}
          />
        )}
      </this.Compose>
    );
  }
}

/**
 * CreditBundlesListContainer PropTypes
 * @param initialFilters - prop initial Filters to apply
 */

CreditBundlesListContainer.propTypes = {
  initialFilters: PropTypes.shape({}).isRequired,
  me: PropTypes.instanceOf(Object).isRequired,
};

export default withApollo(withTranslation()(CreditBundlesListContainer));
